import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth/services/auth.service';
import { AssistantEvent, AssistantProviderKey } from '../models/assistant.model';

/** The confirm/reject REST response - a resolved proposal, never the model's own words. */
export interface AssistantProposalOutcome {
  id: string;
  status: 'pending' | 'executed' | 'rejected' | 'failed' | 'expired';
  errorMessage?: string;
}

/** One prior turn, replayed to the backend so a follow-up can say "and the other depot?". */
export interface AssistantHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Client for the assistant's streaming endpoint.
 *
 * `HttpClient` is used everywhere else in this module and is not used here, for two reasons
 * that both have to hold:
 *
 *  - `EventSource` cannot be used at all. It is GET-only and cannot set an `Authorization`
 *    header, and this endpoint is a POST behind the same bearer token as the rest of the API.
 *  - `HttpClient` can be made to surface partial text with `observe: 'events'` and
 *    `responseType: 'text'`, but it hands back the whole response body accumulated so far on
 *    every progress event, so the caller has to diff strings to find what is new. `fetch` with
 *    a stream reader hands over exactly the bytes that arrived.
 *
 * The cost of leaving `HttpClient` is that `AuthInterceptor` does not fire, so the bearer
 * token is attached here by hand. If the token handling in `AuthService` changes, this is the
 * second place that has to change.
 */
@Injectable({
  providedIn: 'root'
})
export class AssistantService {

  /**
  * Endpoint for http calls
  */
  private _endpoint: string = environment.api.endpoint;

  constructor(private zone: NgZone, private authService: AuthService, private httpClient: HttpClient) { }

  /**
   * Confirms a pending proposal - the only call in this service that changes anything, and only
   * because a human clicked the button that led to it. Plain `HttpClient`, unlike `ask()`: this is
   * one JSON response, not a stream, so `AuthInterceptor` attaching the bearer token is enough and
   * there is no need for the manual `fetch` machinery above.
   */
  confirmProposal(id: string): Observable<AssistantProposalOutcome> {
    return this.httpClient.post(this._endpoint + 'assistant/proposals/' + id + '/confirm', {})
      .pipe(map((value: any) => <AssistantProposalOutcome>value));
  }

  /** Rejects a pending proposal. Nothing is applied. */
  rejectProposal(id: string): Observable<AssistantProposalOutcome> {
    return this.httpClient.post(this._endpoint + 'assistant/proposals/' + id + '/reject', {})
      .pipe(map((value: any) => <AssistantProposalOutcome>value));
  }

  /**
   * Asks one question and streams the answer back a frame at a time.
   *
   * The observable emits one `AssistantEvent` per SSE frame and then completes. It always
   * completes: a mid-answer `error` frame, a non-2xx response, a dropped socket and a stream
   * that stops without a `done` frame all arrive as an `error` event followed by completion,
   * so a caller that clears its spinner on completion cannot be left spinning.
   *
   * Unsubscribing aborts the request in flight.
   */
  ask(question: string,
    history: AssistantHistoryMessage[],
    provider: AssistantProviderKey): Observable<AssistantEvent> {

    return new Observable<AssistantEvent>((subscriber: Subscriber<AssistantEvent>) => {
      const controller: AbortController = new AbortController();

      this.stream(question, history, provider, controller.signal, subscriber);

      return () => controller.abort();
    });
  }

  /**
   * Runs the request and pumps the response body into `subscriber`.
   *
   * Deliberately `async void` and deliberately cannot throw: every failure path below turns
   * into an `error` event and a completion, because a rejected promise here would be an
   * unhandled rejection with the screen still showing a spinner.
   */
  private async stream(question: string,
    history: AssistantHistoryMessage[],
    provider: AssistantProviderKey,
    signal: AbortSignal,
    subscriber: Subscriber<AssistantEvent>): Promise<void> {

    /** Set by a `done` or `error` frame. Stops the end-of-stream check firing a second error. */
    let settled: boolean = false;

    const emit = (event: AssistantEvent) => {
      // zone.js patches `fetch`, but it does not patch `ReadableStream`, so the continuation
      // after `await reader.read()` runs outside the Angular zone and nothing it touches is
      // picked up by change detection. Without this the whole answer appears at once when
      // some unrelated event happens to tick the zone — which is exactly the failure the
      // streaming was built to avoid, and it looks like a backend problem rather than this.
      this.zone.run(() => subscriber.next(event));
    };

    const fail = (message: string) => {
      if (settled) {
        return;
      }

      settled = true;
      emit({ type: 'error', message: message });
    };

    /**
     * Records that the backend said how the turn ended.
     *
     * Without this the end-of-stream check below fires on every stream, including the ones
     * that ended properly — so a `done` frame was followed by a bogus "connection ended", and
     * a provider's own error message ("529: overloaded") was immediately overwritten by it.
     * The screen then blamed the network for something the model had already explained.
     */
    const settle = () => { settled = true; };

    try {
      const headers: any = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream'
      };

      // AuthInterceptor is bypassed by leaving HttpClient, so the token goes on by hand.
      if (this.authService.isLoggedIn()) {
        headers['Authorization'] = 'Bearer ' + this.authService.getToken();
      }

      const response: Response = await fetch(this._endpoint + 'assistant/ask/', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          question: question,
          history: history ? history : [],
          provider: provider
        }),
        signal: signal
      });

      if (!response.ok) {
        // The error body is whatever Nest serialised, and it is not necessarily SSE. Read it
        // as text and show the status, rather than letting a 401 render as an empty answer.
        const detail: string = await this.readErrorBody(response);

        // The detail is the backend's own sentence and usually ends in a full stop already,
        // so one is only added when it does not — otherwise it reads "...is required..".
        fail('The assistant could not be reached (HTTP ' + response.status + ')'
          + (detail ? ': ' + detail : '')
          + (/[.!?]$/.test(detail) ? '' : '.'));
        this.zone.run(() => subscriber.complete());
        return;
      }

      if (!response.body) {
        fail('The assistant replied without a readable body.');
        this.zone.run(() => subscriber.complete());
        return;
      }

      const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();

      // `stream: true` so a multi-byte character split across two network chunks is held
      // back rather than decoded as two replacement characters. A product name with an
      // accent in it is enough to hit this.
      const decoder: TextDecoder = new TextDecoder('utf-8');

      /**
       * Bytes that have arrived but do not yet make up a whole frame.
       *
       * A frame is NOT a chunk. The socket splits wherever it likes, so one `read()` can carry
       * two frames, half a frame, or the tail of one and the head of the next. Only complete
       * frames are cut out of this buffer; the remainder waits for more bytes. Parsing on chunk
       * boundaries would hand `JSON.parse` half an object part way through an answer.
       */
      let buffer: string = '';

      while (true) {
        const result = await reader.read();

        if (result.done) {
          break;
        }

        buffer += decoder.decode(result.value, { stream: true });

        const separator: RegExp = /\r\n\r\n|\n\n|\r\r/;
        let match: RegExpExecArray = separator.exec(buffer);

        while (match) {
          const frame: string = buffer.substring(0, match.index);

          buffer = buffer.substring(match.index + match[0].length);

          if (!this.handleFrame(frame, emit, fail, settle)) {
            // A frame we could not parse. `handleFrame` has already reported it; stop reading
            // rather than carry on appending text to an answer we know is damaged.
            await reader.cancel().catch(() => undefined);
            this.zone.run(() => subscriber.complete());
            return;
          }

          match = separator.exec(buffer);
        }
      }

      // Flush the decoder and process anything left that was never terminated. A server that
      // closes without a trailing blank line still gets its last frame read.
      buffer += decoder.decode();

      if (buffer.trim() !== '') {
        this.handleFrame(buffer, emit, fail, settle);
      }

      // The socket closed. If no `done` and no `error` frame ever arrived the answer is
      // truncated, and saying so is the difference between a visibly incomplete answer and
      // one that silently reads as finished.
      // Says what happened and stops there. The screen wraps every failure in "This answer did
      // not finish" and adds the don't-act-on-it caveat when there is partial text to act on,
      // so repeating it here printed the same warning twice in one red box.
      fail('The connection to the assistant ended before the answer was finished.');

      this.zone.run(() => subscriber.complete());

    } catch (e) {
      // An abort is the caller cancelling on purpose — not something to report as a failure.
      if (signal.aborted || (e && e.name === 'AbortError')) {
        this.zone.run(() => subscriber.complete());
        return;
      }

      fail(this.describe(e));
      this.zone.run(() => subscriber.complete());
    }
  }

  /**
   * Parses one complete SSE frame and emits whatever it carried.
   *
   * Returns false only when the frame carried a data payload that is not the JSON this
   * endpoint is documented to send — the one case where continuing would be guessing.
   */
  private handleFrame(frame: string,
    emit: (event: AssistantEvent) => void,
    fail: (message: string) => void,
    settle: () => void): boolean {

    const lines: string[] = frame.split(/\r\n|\n|\r/);
    const data: string[] = [];

    lines.forEach((line: string) => {
      // A line starting with a colon is a comment. Proxies and servers send these as
      // keepalives to stop an idle connection being closed; they carry nothing.
      if (line === '' || line.charAt(0) === ':') {
        return;
      }

      const colon: number = line.indexOf(':');
      const field: string = colon === -1 ? line : line.substring(0, colon);

      if (field !== 'data') {
        // `event`, `id` and `retry` are all legal and none of them are used here: the event
        // type lives inside the JSON payload, not in the SSE envelope.
        return;
      }

      let value: string = colon === -1 ? '' : line.substring(colon + 1);

      // Exactly one leading space is part of the framing, not part of the value.
      if (value.charAt(0) === ' ') {
        value = value.substring(1);
      }

      data.push(value);
    });

    if (data.length === 0) {
      return true;
    }

    // Multiple data lines in one frame are joined with a newline, per the SSE format. A
    // payload with a raw newline in it arrives this way rather than as two frames.
    const payload: string = data.join('\n');

    if (payload.trim() === '') {
      return true;
    }

    let event: AssistantEvent = null;

    try {
      event = JSON.parse(payload);
    } catch (e) {
      fail('The assistant sent a reply this screen could not read. The answer above may be '
        + 'incomplete — check the figures on the stock screens.');
      return false;
    }

    if (!event || !event.type) {
      return true;
    }

    if (event.type !== 'text' && event.type !== 'tool' && event.type !== 'proposal'
      && event.type !== 'done' && event.type !== 'error') {
      // An event type added to the backend after this screen was written. Ignoring it keeps
      // the answer readable; acting on it would mean guessing what it means.
      return true;
    }

    // `done` and `error` are both terminal: the backend has said how the turn ended, so the
    // end-of-stream check must not then say something different about it.
    if (event.type === 'done' || event.type === 'error') {
      settle();
    }

    emit(event);

    return true;
  }

  /** The body of a non-2xx response, trimmed to something that fits on a screen. */
  private async readErrorBody(response: Response): Promise<string> {
    try {
      const text: string = await response.text();

      if (!text) {
        return '';
      }

      try {
        const parsed: any = JSON.parse(text);

        if (parsed && parsed.message) {
          return String(parsed.message).substring(0, 300);
        }
      } catch (e) {
        // Not JSON. Fall through and show the raw text.
      }

      return text.substring(0, 300);
    } catch (e) {
      return '';
    }
  }

  /**
   * A network failure with no status and no body.
   *
   * `fetch` rejects with a bare "Failed to fetch" for a dropped connection, a refused one and
   * a CORS rejection alike, so the message says what to do rather than pretending to know
   * which of those it was.
   */
  private describe(e: any): string {
    const detail: string = e && e.message ? String(e.message) : '';

    return 'The connection to the assistant failed'
      + (detail ? ' (' + detail + ')' : '') + '.';
  }
}
